<script setup>
import { computed } from "vue";

const props = defineProps({
  invoice: Object,
  amount: [String, Number],
  method: String,
  status: String,
  generateReceipt: Boolean,
  disabled: Boolean,
  submitting: Boolean,
});

defineEmits(["submit"]);

const remainingAfterPayment = computed(() => {
  const outstanding = Number(props.invoice?.outstandingAmount || 0);
  const paid = Number(props.amount || 0);
  return Math.max(outstanding - paid, 0);
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const methodText = (method) => {
  const map = {
    cash: "เงินสด",
    bank_transfer: "โอนธนาคาร",
    qr_payment: "QR Payment",
    credit_card: "บัตรเครดิต",
  };
  return map[method] || "-";
};

const statusText = (status) => {
  const map = {
    completed: "รับชำระสมบูรณ์",
    partial: "ชำระบางส่วน",
    pending_verify: "รอตรวจสอบ",
  };
  return map[status] || "-";
};
</script>
<template>
  <div class="card border-0 shadow-sm rounded-4 sticky-summary">
    <div class="card-body p-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 class="fw-bold mb-1">สรุปรายการ</h5>
          <div class="text-muted small">
            ตรวจสอบข้อมูลก่อนบันทึกรายการรับชำระ
          </div>
        </div>
        <i class="bi bi-clipboard-check text-success fs-4"></i>
      </div>

      <div class="vstack gap-3 mb-4">
        <div class="d-flex justify-content-between">
          <span class="text-muted">ใบแจ้งหนี้</span>
          <span class="fw-semibold">{{ invoice?.invoiceNo || "-" }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">ลูกบ้าน</span>
          <span class="fw-semibold text-end">{{
            invoice?.residentName || "-"
          }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">ยูนิต</span>
          <span class="fw-semibold">{{ invoice?.unitCode || "-" }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">ยอดคงเหลือ</span>
          <span class="fw-semibold text-danger">{{
            formatCurrency(invoice?.outstandingAmount || 0)
          }}</span>
        </div>
      </div>

      <div class="summary-box bg-light rounded-4 p-3 mb-3">
        <div class="text-muted small mb-1">จำนวนเงินที่จะรับชำระ</div>
        <div class="fw-bold fs-4 text-success">
          {{ formatCurrency(amount) }}
        </div>
      </div>

      <div
        class="summary-box rounded-4 p-3 mb-4"
        :class="
          remainingAfterPayment > 0
            ? 'bg-warning bg-opacity-10'
            : 'bg-success bg-opacity-10'
        "
      >
        <div class="text-muted small mb-1">ยอดคงเหลือหลังบันทึก</div>
        <div
          class="fw-bold fs-4"
          :class="
            remainingAfterPayment > 0 ? 'text-warning-emphasis' : 'text-success'
          "
        >
          {{ formatCurrency(remainingAfterPayment) }}
        </div>
      </div>

      <div class="vstack gap-3 mb-4">
        <div class="d-flex justify-content-between">
          <span class="text-muted">ช่องทางชำระ</span>
          <span class="fw-semibold">{{ methodText(method) }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">สถานะรายการ</span>
          <span class="fw-semibold">{{ statusText(status) }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">ออกใบเสร็จ</span>
          <span class="fw-semibold">{{
            generateReceipt ? "ใช่" : "ไม่ใช่"
          }}</span>
        </div>
      </div>

      <button
        type="button"
        class="btn btn-success w-100 rounded-3 py-2 fw-semibold"
        :disabled="disabled || submitting"
        @click="$emit('submit')"
      >
        <span
          v-if="submitting"
          class="spinner-border spinner-border-sm me-2"
        ></span>
        <i v-else class="bi bi-floppy me-1"></i
        >{{ submitting ? "กำลังบันทึก..." : "บันทึกรายการรับชำระ" }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.sticky-summary {
  position: sticky;
  top: 90px;
}
</style>
